/**
 * Native Thermal Printer Service
 * Handles USB thermal printing with auto-detect and auto-connect
 * Removes browser print - only thermal printing supported
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

// Register the native USB printer plugin
const USBThermalPrinter = registerPlugin('USBThermalPrinter');

class NativeThermalPrinterService {
  constructor() {
    this.settings = this.loadSettings();
    this.connectionStatus = {
      connected: false,
      deviceName: null,
      deviceId: null,
      printerAvailable: false
    };
    this.statusListeners = [];
    this.initialized = false;
    
    // Initialize on construction
    this.initialize();
  }

  // ==================== INITIALIZATION ====================

  async initialize() {
    if (this.initialized) return;
    
    if (!Capacitor.isNativePlatform()) {
      console.log('[Printer] Not a native platform, printer features disabled');
      return;
    }
    
    try {
      // Set up listener for USB events from native plugin
      await USBThermalPrinter.addListener('printerStatus', (event) => {
        console.log('[Printer] Status event:', event);
        this.handlePrinterEvent(event);
      });
      
      // Get initial status
      await this.refreshStatus();
      
      this.initialized = true;
      console.log('[Printer] Service initialized');
    } catch (error) {
      console.error('[Printer] Initialization error:', error);
    }
  }

  handlePrinterEvent(event) {
    switch (event.event) {
      case 'connected':
        this.connectionStatus = {
          connected: true,
          deviceName: event.deviceName,
          deviceId: event.deviceId,
          printerAvailable: true
        };
        this.notifyStatusChange();
        break;
        
      case 'disconnected':
        this.connectionStatus = {
          connected: false,
          deviceName: null,
          deviceId: null,
          printerAvailable: this.connectionStatus.printerAvailable
        };
        this.notifyStatusChange();
        break;
        
      case 'attached':
        this.connectionStatus.printerAvailable = true;
        // If we have permission, auto-connect is handled by native plugin
        if (event.hasPermission) {
          // Native plugin will send 'connected' event
        }
        this.notifyStatusChange();
        break;
    }
  }

  // ==================== STATUS MANAGEMENT ====================

  async refreshStatus() {
    if (!Capacitor.isNativePlatform()) {
      return this.connectionStatus;
    }
    
    try {
      const status = await USBThermalPrinter.getStatus();
      this.connectionStatus = {
        connected: status.connected || false,
        deviceName: status.deviceName || status.availableDeviceName || null,
        deviceId: status.deviceId || status.availableDeviceId || null,
        printerAvailable: status.printerAvailable || status.connected || false,
        hasPermission: status.hasPermission || status.availableHasPermission || false
      };
      this.notifyStatusChange();
      return this.connectionStatus;
    } catch (error) {
      console.error('[Printer] Status refresh error:', error);
      return this.connectionStatus;
    }
  }

  getStatus() {
    return this.connectionStatus;
  }

  isConnected() {
    return this.connectionStatus.connected;
  }

  isPrinterAvailable() {
    return this.connectionStatus.printerAvailable;
  }

  // Status change listeners
  addStatusListener(callback) {
    this.statusListeners.push(callback);
    // Immediately call with current status
    callback(this.connectionStatus);
    return () => {
      this.statusListeners = this.statusListeners.filter(cb => cb !== callback);
    };
  }

  notifyStatusChange() {
    this.statusListeners.forEach(cb => {
      try {
        cb(this.connectionStatus);
      } catch (e) {
        console.error('[Printer] Status listener error:', e);
      }
    });
  }

  // ==================== CONNECTION MANAGEMENT ====================

  async connect(deviceId = null) {
    if (!Capacitor.isNativePlatform()) {
      return { success: false, error: 'Not a native platform' };
    }
    
    try {
      const result = await USBThermalPrinter.connect({ deviceId });
      if (result.connected) {
        this.connectionStatus = {
          connected: true,
          deviceName: result.deviceName,
          deviceId: result.deviceId,
          printerAvailable: true
        };
        this.notifyStatusChange();
        return { success: true, deviceName: result.deviceName };
      }
      return { success: false, error: 'Connection failed' };
    } catch (error) {
      console.error('[Printer] Connect error:', error);
      return { success: false, error: error.message };
    }
  }

  async disconnect() {
    if (!Capacitor.isNativePlatform()) {
      return { success: true };
    }
    
    try {
      await USBThermalPrinter.disconnect();
      this.connectionStatus = {
        connected: false,
        deviceName: null,
        deviceId: null,
        printerAvailable: this.connectionStatus.printerAvailable
      };
      this.notifyStatusChange();
      return { success: true };
    } catch (error) {
      console.error('[Printer] Disconnect error:', error);
      return { success: false, error: error.message };
    }
  }

  async requestPermission(deviceId = null) {
    if (!Capacitor.isNativePlatform()) {
      return { success: false, error: 'Not a native platform' };
    }
    
    try {
      const result = await USBThermalPrinter.requestPermission({ deviceId });
      return { success: result.granted, ...result };
    } catch (error) {
      console.error('[Printer] Permission request error:', error);
      return { success: false, error: error.message };
    }
  }

  async scanDevices() {
    if (!Capacitor.isNativePlatform()) {
      return [];
    }
    
    try {
      const result = await USBThermalPrinter.getDevices();
      return result.devices || [];
    } catch (error) {
      console.error('[Printer] Scan error:', error);
      return [];
    }
  }

  // ==================== SETTINGS ====================

  loadSettings() {
    try {
      const saved = localStorage.getItem('printerSettings');
      if (saved) {
        const settings = JSON.parse(saved);
        // Ensure required fields exist
        return {
          ...this.getDefaultSettings(),
          ...settings
        };
      }
    } catch (e) {
      console.error('[Printer] Settings load error:', e);
    }
    return this.getDefaultSettings();
  }

  getDefaultSettings() {
    return {
      paperWidth: '80mm',
      autoCut: true,
      openDrawer: false,
      printCopies: 1,
      footerText: 'Thank you for your visit!',
      // Print options
      showOrderDetails: true,
      showTaxBreakdown: false,
      showCustomerInfo: true,
      fontSize: 'normal'
    };
  }

  getSettings() {
    return this.settings;
  }

  updateSettings(newSettings) {
    this.settings = { ...this.settings, ...newSettings };
    localStorage.setItem('printerSettings', JSON.stringify(this.settings));
    return this.settings;
  }

  // ==================== PRINTING ====================

  async print(orderData, franchiseData) {
    if (!Capacitor.isNativePlatform()) {
      return { success: false, error: 'Printing only supported on Android app' };
    }
    
    if (!this.connectionStatus.connected) {
      // Try to auto-connect
      const connectResult = await this.connect();
      if (!connectResult.success) {
        return { success: false, error: 'Printer not connected. Please connect a USB thermal printer.' };
      }
    }
    
    try {
      const escposData = this.buildReceipt(orderData, franchiseData);
      const base64Data = this.toBase64(escposData);
      
      // Print the specified number of copies
      const copies = this.settings.printCopies || 1;
      for (let i = 0; i < copies; i++) {
        const result = await USBThermalPrinter.print({ data: base64Data });
        if (!result.success) {
          return { success: false, error: 'Print failed' };
        }
        
        // Small delay between copies
        if (i < copies - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      return { success: true, method: 'usb', copies };
    } catch (error) {
      console.error('[Printer] Print error:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== RECEIPT BUILDING (ESC/POS) ====================

  buildReceipt(orderData, franchiseData) {
    const width = this.settings.paperWidth === '58mm' ? 32 : 48;
    const commands = [];
    
    // Initialize printer
    commands.push(0x1B, 0x40); // ESC @ - Initialize
    commands.push(0x1B, 0x61, 0x01); // Center align
    
    // Brand Name (from client record) - Normal size, not too large
    const brandName = franchiseData.brand_name || franchiseData.name || 'FOS';
    commands.push(0x1B, 0x45, 0x01); // Bold on
    commands.push(...this.textToBytes(brandName));
    commands.push(0x0A); // New line
    commands.push(0x1B, 0x45, 0x00); // Bold off
    
    // Separator
    commands.push(...this.textToBytes('─'.repeat(width)));
    commands.push(0x0A);
    
    // Order Number
    if (orderData.order_no) {
      commands.push(0x1B, 0x61, 0x00); // Left align
      commands.push(...this.textToBytes(`Order #: ${orderData.order_no}`));
      commands.push(0x0A);
    }
    
    // Franchise Name (outlet name)
    const outletName = franchiseData.franchise_name || franchiseData.name || '';
    if (outletName && outletName !== brandName) {
      commands.push(...this.textToBytes(`Outlet: ${outletName}`));
      commands.push(0x0A);
    }
    
    // Contact Number
    const contact = franchiseData.contact_number || franchiseData.mobile_number || '';
    if (contact) {
      commands.push(...this.textToBytes(`Tel: ${contact}`));
      commands.push(0x0A);
    }
    
    // Address + Pincode
    const address = franchiseData.franchise_address || franchiseData.address || '';
    const pincode = franchiseData.pincode || '';
    if (address) {
      const fullAddress = pincode ? `${address} - ${pincode}` : address;
      commands.push(...this.textToBytes(fullAddress.substring(0, width)));
      commands.push(0x0A);
    }
    
    // GST Number (only if present)
    const gstin = franchiseData.gstin || '';
    if (gstin) {
      commands.push(...this.textToBytes(`GSTIN: ${gstin}`));
      commands.push(0x0A);
    }
    
    // Date/Time
    const dateStr = orderData.date_timestamp 
      ? new Date(orderData.date_timestamp).toLocaleString('en-IN')
      : new Date().toLocaleString('en-IN');
    commands.push(...this.textToBytes(`Date: ${dateStr}`));
    commands.push(0x0A);
    
    // Order Type & Table/Token (if showOrderDetails is enabled)
    if (this.settings.showOrderDetails) {
      if (orderData.order_type) {
        const typeStr = orderData.order_type.replace('_', ' ').toUpperCase();
        let detailStr = `Type: ${typeStr}`;
        if (orderData.table_no) detailStr += ` | Table: ${orderData.table_no}`;
        if (orderData.token_no) detailStr += ` | Token: ${orderData.token_no}`;
        commands.push(...this.textToBytes(detailStr));
        commands.push(0x0A);
      }
    }
    
    // Customer Info (only if showCustomerInfo enabled AND customer data exists)
    if (this.settings.showCustomerInfo && (orderData.customer_name || orderData.customer_mobile)) {
      if (orderData.customer_name) {
        commands.push(...this.textToBytes(`Customer: ${orderData.customer_name}`));
        commands.push(0x0A);
      }
      if (orderData.customer_mobile) {
        commands.push(...this.textToBytes(`Mobile: ${orderData.customer_mobile}`));
        commands.push(0x0A);
      }
    }
    
    // Separator before items
    commands.push(...this.textToBytes('═'.repeat(width)));
    commands.push(0x0A);
    
    // Items header
    const itemColWidth = width - 16; // Leave space for Qty and Amount
    commands.push(...this.textToBytes(this.padRight('Item', itemColWidth) + this.padLeft('Qty', 4) + this.padLeft('Amt', 8)));
    commands.push(0x0A);
    commands.push(...this.textToBytes('─'.repeat(width)));
    commands.push(0x0A);
    
    // Items
    let subtotal = 0;
    if (orderData.items && orderData.items.length > 0) {
      orderData.items.forEach(item => {
        const name = (item.name || 'Item').substring(0, itemColWidth);
        const qty = item.quantity || 1;
        const lineTotal = item.line_total || (item.price * qty);
        subtotal += lineTotal;
        
        commands.push(...this.textToBytes(
          this.padRight(name, itemColWidth) + 
          this.padLeft(qty.toString(), 4) + 
          this.padLeft(lineTotal.toFixed(2), 8)
        ));
        commands.push(0x0A);
      });
    }
    
    // Totals separator
    commands.push(...this.textToBytes('─'.repeat(width)));
    commands.push(0x0A);
    
    // Subtotal
    commands.push(...this.textToBytes(this.padRight('SUBTOTAL:', width - 10) + this.padLeft(subtotal.toFixed(2), 10)));
    commands.push(0x0A);
    
    // Tax breakdown (if enabled and applicable)
    if (this.settings.showTaxBreakdown && orderData.tax_amount && orderData.tax_amount > 0) {
      const taxRate = orderData.tax_rate || 5;
      const halfRate = taxRate / 2;
      const halfTax = orderData.tax_amount / 2;
      
      commands.push(...this.textToBytes(this.padRight(`CGST (${halfRate}%):`, width - 10) + this.padLeft(halfTax.toFixed(2), 10)));
      commands.push(0x0A);
      commands.push(...this.textToBytes(this.padRight(`SGST (${halfRate}%):`, width - 10) + this.padLeft(halfTax.toFixed(2), 10)));
      commands.push(0x0A);
    }
    
    // Grand Total
    commands.push(...this.textToBytes('─'.repeat(width)));
    commands.push(0x0A);
    const total = orderData.total_value || subtotal;
    commands.push(0x1B, 0x45, 0x01); // Bold on
    commands.push(...this.textToBytes(this.padRight('TOTAL:', width - 12) + this.padLeft('Rs.' + total.toFixed(2), 12)));
    commands.push(0x1B, 0x45, 0x00); // Bold off
    commands.push(0x0A);
    
    // Payment method
    if (orderData.payment_method) {
      const paymentStr = orderData.payment_method.toUpperCase();
      commands.push(...this.textToBytes(this.padRight('Payment:', width - 12) + this.padLeft(paymentStr, 12)));
      commands.push(0x0A);
    }
    
    // Footer separator
    commands.push(...this.textToBytes('═'.repeat(width)));
    commands.push(0x0A);
    
    // Footer text (editable by user)
    commands.push(0x1B, 0x61, 0x01); // Center align
    const footerText = this.settings.footerText || 'Thank you for your visit!';
    commands.push(...this.textToBytes(footerText));
    commands.push(0x0A);
    commands.push(...this.textToBytes('Visit Again!'));
    commands.push(0x0A);
    
    // Final separator
    commands.push(...this.textToBytes('═'.repeat(width)));
    commands.push(0x0A);
    
    // Feed and cut
    commands.push(0x0A, 0x0A, 0x0A); // Feed 3 lines
    
    if (this.settings.autoCut) {
      commands.push(0x1D, 0x56, 0x00); // Full cut
    }
    
    if (this.settings.openDrawer) {
      commands.push(0x1B, 0x70, 0x00, 0x19, 0xFA); // Open cash drawer
    }
    
    return new Uint8Array(commands);
  }

  // Helper methods for receipt formatting
  textToBytes(text) {
    const bytes = [];
    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i);
      if (charCode < 128) {
        bytes.push(charCode);
      } else {
        // For non-ASCII, use placeholder or skip
        bytes.push(0x3F); // ?
      }
    }
    return bytes;
  }

  padRight(str, len) {
    return str.substring(0, len).padEnd(len, ' ');
  }

  padLeft(str, len) {
    return str.substring(0, len).padStart(len, ' ');
  }

  toBase64(uint8Array) {
    let binary = '';
    for (let i = 0; i < uint8Array.length; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }
    return btoa(binary);
  }

  // ==================== TEST PRINT ====================

  async testPrint(franchiseData) {
    const testOrder = {
      order_no: 'TEST-001',
      date_timestamp: new Date().toISOString(),
      order_type: 'dine_in',
      table_no: '5',
      customer_name: 'Test Customer',
      customer_mobile: '9876543210',
      items: [
        { name: 'Filter Coffee', quantity: 2, price: 18, line_total: 36 },
        { name: 'Masala Tea', quantity: 1, price: 20, line_total: 20 },
        { name: 'Butter Biscuit', quantity: 3, price: 5, line_total: 15 }
      ],
      total_value: 71,
      payment_method: 'CASH'
    };
    
    return await this.print(testOrder, franchiseData);
  }
}

// Singleton instance
const nativeThermalPrinter = new NativeThermalPrinterService();
export default nativeThermalPrinter;
